import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { User, Menu, Package } from "lucide-react";
import { getStoredUser } from "../../../services/api.js";
import { logout } from "../../../services/authService.js";
import { useApp } from "../../../hooks/useApp.js";

const ROUTE_TITLES = {
  "/dashboard": "Dashboard",
  "/dashboard/filings": "My Filings",
  "/dashboard/businesses": "Businesses",
  "/dashboard/preparers": "Tax Preparers",
  "/dashboard/payments": "Payment History",
  "/dashboard/referrals": "Referral Credit",
  "/dashboard/settings": "Settings",
};

function getTitleFromPath(pathname) {
  return ROUTE_TITLES[pathname] || "Dashboard";
}

export default function Topbar({ onMobileMenuToggle }) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const title = getTitleFromPath(location.pathname);

  const user = getStoredUser() || {};

  // Resets AppContext state (and its sessionStorage-persisted businessId/
  // preparerId/returnId - see AppContext.jsx) so the next sign-in in this
  // tab never inherits this account's business/return.
  const handleSignOut = async () => {
    await logout();
    dispatch({ type: "SEED", which: "new" });
    navigate("/login");
  };

  return (
    <header className="bg-[var(--panel)] border-b border-[var(--line)] px-7 h-[70px] flex items-center justify-between sticky top-0 z-50 flex-shrink-0">
      {/* ── Left ── */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMobileMenuToggle}
          className="md:hidden w-9 h-9 rounded-[8px] bg-[var(--panel-2)] flex items-center justify-center cursor-pointer hover:bg-[var(--line)] transition-all border-none text-[var(--ink)]"
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>
        <nav className="flex items-center gap-[6px] text-[14px]">
          <span className="text-[var(--muted)]">EasyFile2290</span>
          <span className="text-[var(--dim)]">/</span>
          <span className="font-semibold text-[var(--ink)]">{title}</span>
        </nav>
      </div>

      {/* ── Right ── */}
      <div className="flex items-center gap-3">
        {/* Animated-border CTA to the Subscriptions page - the spinning conic
            gradient lives on an oversized absolutely-positioned span behind
            the label; the wrapper's padding + overflow-hidden clip it down to
            a thin rotating ring, so only the border itself appears in motion. */}
        <Link
          to="/dashboard/subscriptions"
          className="relative inline-flex items-center rounded-[10px] p-[1.5px] overflow-hidden flex-shrink-0"
        >
          <span
            className="absolute inset-[-150%] animate-border-spin"
            style={{
              background:
                "conic-gradient(from 0deg, var(--accent), transparent 35%, transparent 65%, var(--accent))",
            }}
          />
          <span className="relative flex items-center gap-1.5 bg-[var(--panel)] text-[var(--ink)] text-[13px] font-semibold px-3.5 py-[7px] rounded-[9px] hover:bg-[var(--panel-2)] transition-colors">
            <Package size={14} />
            <span className="hidden sm:inline">Subscriptions</span>
          </span>
        </Link>

        <div className="relative">
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center gap-2 px-[10px] py-[5px] bg-[var(--panel-2)] border border-[var(--line)] rounded-[8px] cursor-pointer hover:border-[var(--line-2)] transition-all"
          >
            <div className="w-7 h-7 rounded-[7px] bg-[var(--accent)] flex items-center justify-center text-white flex-shrink-0">
              <User size={15} />
            </div>
            <span className="text-[11px] text-[var(--muted)]">▾</span>
          </button>

          {userMenuOpen && (
            <div
              className="absolute right-0 top-[calc(100%+6px)] w-[220px] bg-[var(--panel)] border border-[var(--line)] rounded-[10px] shadow-[var(--shadow)] py-1 z-[200]"
              onMouseLeave={() => setUserMenuOpen(false)}
            >
              <div className="px-4 py-2.5 border-b border-[var(--line)]">
                <p className="text-[12px] text-[var(--muted)] truncate">{user.email || ""}</p>
              </div>
              <Link
                to="/dashboard/settings"
                onClick={() => setUserMenuOpen(false)}
                className="block px-4 py-2 text-[13px] text-[var(--ink)] no-underline hover:bg-[var(--panel-2)] transition-colors"
              >
                Settings
              </Link>
              <div className="border-t border-[var(--line)] mt-1">
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-[13px] text-[var(--danger)] hover:bg-[var(--danger-soft)] transition-colors bg-transparent border-none cursor-pointer"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
